import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ForcePasswordResetModel } from '@app/models/auth/forcePasswordReset.model';
import { PasswordResetResponse } from '@app/models/auth/passwordResetResponse.model';
import { AuthService } from '@app/services/auth.service';
import { LocalisationService } from '@app/services/localisation.service';

import { AppConfig } from './../../../../../app/app.config';
import { Globals } from './../../../../../globals';
import { equalsValidator } from './../../../../directives/equals-validator.directive';
import { WelcomeComponent } from './../../..//welcome/welcome.component';
import { CourseAreaListComponent } from './../../../course-areas/list/course-area-list/course-area-list.component';

@Component({
  selector: 'app-base-manage-password',
  templateUrl: './base-manage-password.component.html',
  styleUrls: ['./base-manage-password.component.scss']
})
export class BaseManagePasswordComponent implements OnInit {
  @Input()
  templateSettings; // Passed from components such as account-settings and change-password

  // Template settings
  changePasswordMessage: string = null;
  headerLegend: boolean = false;
  showMessagesTop: boolean = false;
  showMessagesBottom: boolean = false;

  // Standard variables
  static routeParams: string = '?validation=';

  locale: any = {};

  resetPasswordForm: FormGroup;
  resetPasswordSubmitted: boolean = false;
  passwordLength: number;

  validationToken: string;

  showCurrentPassword: boolean = false;
  currentPasswordType: string = 'password';
  showPassword: boolean = false;
  passwordType: string = 'password';
  showConfirmPassword: boolean = false;
  confirmPasswordType: string = 'password';
  passwordToggleText: string = 'Show';

  formSubmitted: boolean = false;

  messageCopy: string = null;
  messageClass: string = this.globals.styles.alertClass;

  constructor(
    public globals: Globals,
    public route: ActivatedRoute,
    public router: Router,
    public localisation: LocalisationService,
    public authService: AuthService,
    public formBuilder: FormBuilder
  ) {}

  ngOnInit() {
    this.locale = this.localisation.getTranslationsForComponent('BaseManagePasswordComponent');
    this.headerLegend = this.templateSettings.headerLegend;
    this.showMessagesTop = this.templateSettings.showMessagesTop;
    if (this.templateSettings.changePasswordMessage == true) {
      this.changePasswordMessage = this.locale.passwordExpiredMessage; // Tell the user password needs changing
    }

    this.passwordLength = AppConfig.passwordLength;

    this.route.queryParams.subscribe(params => {
      this.resetPasswordForm = this.formBuilder.group(
        {
          currentPassword: [null, [Validators.required]],
          password: [null, [Validators.required, Validators.minLength(this.passwordLength)]],
          passwordConfirmation: [null, [Validators.required]]
        },
        {
          validator: equalsValidator('password', 'passwordConfirmation')
        }
      );
    });
  }

  callLocale() {
    this.locale = this.localisation.getTranslationsForComponent('BaseManagePasswordComponent');
  }

  setFormSubmitted(value) {
    this.formSubmitted = value;
  }

  resetPassword(model: ForcePasswordResetModel) {
    this.resetPasswordSubmitted = true;

    if (!this.resetPasswordForm.valid) {
      return;
    }
    this.setFormSubmitted(true);
    this.sendForceResetForm(model);
  }

  sendForceResetForm(model: any) {
    // Reset the form ready for new input
    this.setFormSubmitted(false);
    this.resetPasswordSubmitted = false;
    this.resetPasswordForm.reset();

    this.authService.forceResetPassword(model).subscribe(
      succeed => {
        // If we come from account settings stay on same page
        if (this.templateSettings.redirectOnSuccess == false) {
          this.messageCopy = this.locale.passwordSuccessModalMessage;
          this.messageClass = this.globals.styles.alertClass;
          // If areas are seen go to area list component
        } else if (sessionStorage.getItem('areas-seen') == 'true') {
          this.router.navigate([`/centigrade/${CourseAreaListComponent.route}`]);
          // Otherwise we jump to welcome
        } else {
          this.router.navigate([`/${WelcomeComponent.route}`]);
        }
      },
      (err: PasswordResetResponse) => {
        this.messageCopy = err.returnCode.message;
        this.messageClass = this.globals.styles.errorClass;
      }
    );
  }

  toggleCurrentPassword() {
    this.showCurrentPassword = !this.showCurrentPassword;
    this.currentPasswordType = this.showCurrentPassword ? 'text' : 'password';
  }

  togglePassword() {
    this.showPassword = !this.showPassword;
    this.passwordType = this.showPassword ? 'text' : 'password';
  }

  toggleConfirmPassword() {
    this.showConfirmPassword = !this.showConfirmPassword;
    this.confirmPasswordType = this.showConfirmPassword ? 'text' : 'password';
  }

  validate() {
    if (
      this.resetPasswordForm.controls['passwordConfirmation'].value ==
      this.resetPasswordForm.controls['password'].value
    ) {
      let error = { unequalValues: false };
      this.resetPasswordForm.controls['passwordConfirmation'].setErrors(error);
      this.resetPasswordForm.controls['passwordConfirmation'].updateValueAndValidity();
    }
  }

  goToMain() {
    this.router.navigate([`/centigrade`]);
  }

  logout() {
    this.authService.logout();
    this.goToMain();
  }

  // Clear messages when input fields are active
  onClickInput() {
    this.messageCopy = null;
  }

  setFormGroup(showingForm: string = 'forceResetForm') {
    // Change Password Form
    this.resetPasswordForm = this.formBuilder.group(
      {
        currentPassword: [null, [Validators.required, Validators.minLength(this.passwordLength)]],
        password: [null, [Validators.required, Validators.minLength(this.passwordLength)]],
        passwordConfirmation: [null, [Validators.required]]
      },
      {
        validator: equalsValidator('password', 'passwordConfirmation')
      }
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/components/_shared/account/base-manage-password/base-manage-password.component.ts